import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Employee, EmployeeEvaluation } from "@/types/employee";
import EmployeeDetail from "@/components/EmployeeDetail";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

const EmployeeDetailPage = () => {
  const { employeeId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [evaluations, setEvaluations] = useState<EmployeeEvaluation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!employeeId) {
        navigate('/');
        return;
      }

      if (!user) return;

      const { data: empData, error: empError } = await supabase
        .from('employees')
        .select('*')
        .eq('id', employeeId)
        .single();

      if (empError || !empData) {
        navigate('/');
        return;
      }

      setEmployee({
        id: empData.id,
        name: empData.name,
        position: empData.position,
        employeeNumber: empData.employee_number || '',
        startDate: empData.created_at.split('T')[0]
      });

      const { data: evalData } = await supabase
        .from('evaluations')
        .select(`*, profiles!evaluations_created_by_fkey (full_name), criteria_ratings (*, rating_comments (*))`)
        .eq('employee_id', employeeId)
        .order('date', { ascending: false });

      if (evalData) {
        const transformedEvaluations: EmployeeEvaluation[] = evalData.map(evaluation => {
          const criteriaObj: any = {};
          evaluation.criteria_ratings.forEach((rating: any) => {
            criteriaObj[rating.criteria_key] = {
              rating: rating.rating,
              points: rating.points,
              comments: rating.rating_comments.map((comment: any) => ({
                text: comment.text,
                date: comment.created_at,
                userId: comment.created_by,
                userName: 'Bewerter'
              }))
            };
          });

          return {
            id: evaluation.id,
            employeeId: evaluation.employee_id,
            month: evaluation.month,
            date: evaluation.date,
            totalScore: Number(evaluation.total_score),
            maxScore: evaluation.max_score,
            performanceLevel: evaluation.performance_level,
            criteria: criteriaObj,
            createdBy: evaluation.created_by,
            createdByName: (evaluation.profiles as any)?.full_name || 'Unbekannt'
          };
        });
        setEvaluations(transformedEvaluations);
      }

      setLoading(false);
    };

    loadData();
  }, [employeeId, user, navigate]);

  if (loading || !employee) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <EmployeeDetail employee={employee} evaluations={evaluations} />
    </div>
  );
};

export default EmployeeDetailPage;
